import { c as _c } from "react/compiler-runtime";
import { COUNTRIES } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode, Locale } from '@onefootprint/request-types';
import type { ParseKeys } from 'i18next';
import { useId } from 'react';
import { Controller, type ControllerProps, type FieldValues, type Path, type PathValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BaseSelect, { type BaseSelectOption } from '../../internal/base-select';
import BaseSelectTrigger from '../../internal/base-select-trigger';
import FormWrapper from '../internals/form-wrapper';
import { getCountriesWithCallingCode } from '../utils/tel-utils';
import type { FormInputProps } from './form-input';
export type CountrySelectOption = {
  label: string;
  value: Iso3166TwoDigitCountryCode;
};
export type FormCountryProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: ControllerProps<TFieldValues>['rules'];
  onChange?: (value: Iso3166TwoDigitCountryCode) => void;
  onBlur?: () => void;
  locale?: Locale;
  defaultValue?: Iso3166TwoDigitCountryCode;
  /** Accepted for API compatibility; the react-hook-form field value is the source of truth. */
  value?: Iso3166TwoDigitCountryCode;
  options?: CountrySelectOption[];
  showCallingCode?: boolean;
  searchPlaceholder?: string;
  emptyStateText?: string;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'onChange' | 'onBlur' | 'defaultValue' | 'value' | 'mask'>;
export default function FormCountry(t0) {
  const $ = _c(32);
  const {
    rules,
    name,
    label,
    hint,
    addon,
    errorPosition: t1,
    orientation: t2,
    size: t3,
    id: baseId,
    onChange,
    onBlur,
    defaultValue,
    disabled,
    showCallingCode: t4,
    options: t5,
    searchPlaceholder,
    emptyStateText,
    placeholder,
    className,
    "aria-label": ariaLabel
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const size = t3 === undefined ? "default" : t3;
  const showCallingCode = t4 === undefined ? false : t4;
  const options = t5 === undefined ? COUNTRIES : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      keyPrefix: "components.country-select"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t6);
  const {
    t: tCountries
  } = useTranslation("ui");
  const {
    control
  } = useFormContext();
  const contextId = useId();
  const id = baseId || contextId;
  let t7;
  if ($[1] !== options || $[2] !== showCallingCode || $[3] !== tCountries) {
    let t8;
    if ($[5] !== tCountries) {
      t8 = option => ({
        ...option,
        label: tCountries(`global.countries.${option.value}` as ParseKeys<'ui'>, {
          defaultValue: option.label
        })
      });
      $[5] = tCountries;
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    const translatedOptions = options.map(t8);
    t7 = showCallingCode ? getCountriesWithCallingCode(translatedOptions) : translatedOptions;
    $[1] = options;
    $[2] = showCallingCode;
    $[3] = tCountries;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const countriesOptions = t7 as BaseSelectOption<Iso3166TwoDigitCountryCode>[];
  const selectSize = size === "compact" ? "compact" : "default";
  const t8 = defaultValue as PathValue<TFieldValues, Path<TFieldValues>>;
  let t9;
  if ($[7] !== addon || $[8] !== ariaLabel || $[9] !== className || $[10] !== countriesOptions || $[11] !== disabled || $[12] !== emptyStateText || $[13] !== errorPosition || $[14] !== hint || $[15] !== id || $[16] !== label || $[17] !== onBlur || $[18] !== onChange || $[19] !== orientation || $[20] !== placeholder || $[21] !== searchPlaceholder || $[22] !== selectSize || $[23] !== size || $[24] !== t) {
    t9 = t10 => {
      const {
        field,
        fieldState
      } = t10;
      const error = fieldState.error?.message;
      const isDisabled = disabled || field.disabled;
      const selectedOption = countriesOptions.find(option_0 => option_0.value === field.value);
      const resolvedPlaceholder = placeholder || t(_temp);
      return <FormWrapper id={id} error={error} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={true} disabled={isDisabled}><BaseSelect id={id} options={countriesOptions} value={selectedOption} hasError={!!error} disabled={isDisabled} size={selectSize} searchPlaceholder={searchPlaceholder} emptyStateText={emptyStateText} onChange={option_1 => {
          field.onChange(option_1.value as PathValue<TFieldValues, Path<TFieldValues>>);
          onChange?.(option_1.value);
        }} onBlur={() => {
          field.onBlur();
          onBlur?.();
        }} renderTrigger={t11 => {
          const {
            isOpen,
            onClick,
            testID
          } = t11;
          return <BaseSelectTrigger id={id} testID={testID} className={className} onClick={onClick} disabled={isDisabled} hasError={!!error} hasFocus={isOpen} isEmpty={!selectedOption} size={size} aria-label={label ? undefined : ariaLabel || resolvedPlaceholder}>{selectedOption ? selectedOption.label : resolvedPlaceholder}</BaseSelectTrigger>;
        }} /></FormWrapper>;
    };
    $[7] = addon;
    $[8] = ariaLabel;
    $[9] = className;
    $[10] = countriesOptions;
    $[11] = disabled;
    $[12] = emptyStateText;
    $[13] = errorPosition;
    $[14] = hint;
    $[15] = id;
    $[16] = label;
    $[17] = onBlur;
    $[18] = onChange;
    $[19] = orientation;
    $[20] = placeholder;
    $[21] = searchPlaceholder;
    $[22] = selectSize;
    $[23] = size;
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== control || $[27] !== name || $[28] !== rules || $[29] !== t8 || $[30] !== t9) {
    t10 = <Controller control={control} name={name} rules={rules} defaultValue={t8} render={t9} />;
    $[26] = control;
    $[27] = name;
    $[28] = rules;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp($) {
  return $["placeholder-default"];
}